import React, { CSSProperties, Fragment } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Lock, MoveLeft, MoveRight } from 'lucide-react';
import { DynamicIcon, IconName } from 'lucide-react/dynamic';

import { useGetSimulationWithStages } from '../../hooks/simulations';

import { ESimulationKey, ESimulationStageKey, ESimulationStageStatus } from '../../types/entities.enum';
import { ISimulationStageWithStatus } from '../../types/services.interface';

import { getSimulationColor } from '../../helpers/misc.helper';

export const Stages = () => {
    const { simulationKey } = useParams();

    const navigate = useNavigate();

    const { data: details, isLoading } = useGetSimulationWithStages(simulationKey ? simulationKey as ESimulationKey : null);

    const handleConversation = (stageKey: ESimulationStageKey, action: 'start' | 'continue' | 'retry') => {
        if (!simulationKey || !details) return;

        navigate(`/simulation/${simulationKey}/stages/${stageKey}/details/${action}`);
    };

    const onViewAnalysis = () => {
        if (!simulationKey || !details) return;

        if (details.stages.some(stage => stage.status !== ESimulationStageStatus.COMPLETED)) return;

        navigate(`/simulation/${simulationKey}/analysis`);
    };

    const isStageLocked = (idx: number, stage: ISimulationStageWithStatus): boolean => {
        if (!details) return true;

        return idx > 0 && details.stages[idx - 1].status !== ESimulationStageStatus.COMPLETED && stage.status === ESimulationStageStatus.NOT_STARTED;
    };

    return (
        <div
            className='stages__page'
            style={{ '--simulation-theme-color': getSimulationColor(simulationKey ? simulationKey as ESimulationKey : null) } as CSSProperties}
        >
            {isLoading && !details && (
                <div className='d-flex flex-column align-items-center justify-content-center gap-sm'>
                    <div className='list-loader' />
                    <p className='text-medium color--gray-700'>Loading stages</p>
                </div>
            )}
            {!isLoading && !details && (
                <p className='text-lg text-semibold color--gray-600 text-center'>No stages found!</p>
            )}
            {!isLoading && !!details && (
                <Fragment>
                    <div className='stages__top_sec'>
                        <div className='stages__top_sec__icon'>
                            <DynamicIcon name={details.icon as IconName} />
                        </div>
                        <p className='text-3xl text-bold mb-xs'>{details.title}</p>
                        <p className='color--gray-600'>
                            {details.stages.length === 1
                                ? 'Are you up for the challenge?'
                                : 'Choose a conversation to practice your stakeholder management skills'
                            }
                        </p>
                    </div>

                    <div className='card card--md stages__scenario mb-2xl'>
                        <p className='text-lg text-semibold mb-xl'><span className='color--gray-700'>Scenario:</span> {details.title}</p>
                        <div className='stages__scenario__situation'>
                            <p className='text-semibold mb-xs'>Situation:</p>
                            <div className='stages__scenario__situation__text' dangerouslySetInnerHTML={{ __html: details.situation }} />
                        </div>
                    </div>

                    <p className='text-sm text-semibold color--gray-600 mb-lg tracking-wider'>CHOOSE A PERSONA</p>
                    <div className='stages__list'>
                        {details.stages.map((stage, idx) => (
                            <div
                                key={stage.key}
                                className={`card card--md stages__list__stage ${isStageLocked(idx, stage) ? 'stages__list__stage--locked' : 'card--hover-shadow'}`}
                            >
                                <div className='stages__list__stage__icon'>
                                    <DynamicIcon name={stage.icon as IconName} />
                                </div>
                                <div className='w-full'>
                                    <div className='d-flex justify-content-between gap-lg'>
                                        <div>
                                            <div className='d-flex align-items-center gap-sm'>
                                                <p className='text-xl text-bold'>{stage.person}</p>
                                                {stage.status === ESimulationStageStatus.COMPLETED &&
                                                    <p className='br-full px-sm py-xs text-xs text-medium color--success bg--success-t10'>Completed</p>
                                                }
                                            </div>
                                            <p className='color--gray-700 mb-sm'>{stage.title}</p>
                                        </div>
                                        <div>
                                            {
                                                isStageLocked(idx, stage)
                                                    ? <div className='d-flex align-items-center gap-xs br-full px-md py-xs bg--gray-700-t10'>
                                                        <Lock className='w-md h-md color--gray-600' />
                                                        <p className='text-sm color--gray-600 text-medium'>Locked</p>
                                                    </div>
                                                    : <Fragment>
                                                        {stage.status === ESimulationStageStatus.NOT_STARTED &&
                                                            <button
                                                                onClick={() => handleConversation(stage.key, 'start')}
                                                                className='btn btn--lg btn--primary'
                                                            >
                                                                Start
                                                            </button>
                                                        }
                                                        {stage.status === ESimulationStageStatus.IN_PROGRESS &&
                                                            <button
                                                                onClick={() => handleConversation(stage.key, 'continue')}
                                                                className='btn btn--lg btn--primary'
                                                            >
                                                                Continue
                                                            </button>
                                                        }
                                                        {stage.status === ESimulationStageStatus.COMPLETED &&
                                                            <button
                                                                onClick={() => handleConversation(stage.key, 'retry')}
                                                                className='btn btn--lg btn--secondary'
                                                            >
                                                                Retry
                                                            </button>
                                                        }
                                                    </Fragment>
                                            }
                                        </div>
                                    </div>
                                    <p className='text-sm color--gray-600'>{stage.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Fragment>
            )}

            <div className='stages__actions'>
                <Link to='/' className='link link--md link--primary'>
                    <MoveLeft />
                    <span>Back to Home</span>
                </Link>
                <button
                    className='btn btn--lg btn--primary'
                    disabled={details?.stages.some(stage => stage.status !== ESimulationStageStatus.COMPLETED)}
                    onClick={onViewAnalysis}
                >
                    View Complete Analysis
                    <MoveRight />
                </button>
            </div>
        </div>
    );
};